import { createSelector } from "@reduxjs/toolkit";

import { FormId } from "../../../../components/enums";
import { RootState } from "../../../../store";
import { UIState } from "../../../../store/HOC/genericUI.slice";
import {
  generateAddress,
  generatePhone,
  mapPersonTypeToLabel,
} from "../../../../utilities/forms";

import { ComponentId as CompanyComponentId } from "./CompanyData/enums";
import { ComponentId as ContactPersonComponentId } from "./ContactPersonData/enums";
import { ComponentId as OwnerComponentId } from "./OwnerData/enums";
import { ComponentId as PersonComponentId } from "./PersonData/enums";
import { ComponentId as VehicleComponentId } from "./VehicleData/enums";
import {
  VehicleOwnerOwnerFormInputs,
  VehicleOwnerVehicleFormInputs,
  VehicleOwnerCompanyFormInputs,
  VehicleOwnerPersonFormInputs,
  VehicleOwnerContactPersonFormInputs,
  FormDataInputs,
} from "./vehicleOwner.slice";

export const selectShouldNotValidateByPesel = (store: RootState): boolean =>
  store.ocVehicle.vehicleOwner.data[FormId.VEHICLE_OWNER_PERSON_DATA][
    PersonComponentId.PERSON_DATA_LACK_OF_PESEL
  ] === "true";

export const selectVehicleOwnerVehicleData = (
  store: RootState
): VehicleOwnerVehicleFormInputs =>
  store.ocVehicle.vehicleOwner.data[FormId.VEHICLE_OWNER_VEHICLE];

export const selectVehicleOwnerOwnerData = (
  store: RootState
): VehicleOwnerOwnerFormInputs =>
  store.ocVehicle.vehicleOwner.data[FormId.VEHICLE_OWNER_OWNER];

export const selectVehicleOwnerCompanyData = (
  store: RootState
): VehicleOwnerCompanyFormInputs =>
  store.ocVehicle.vehicleOwner.data[FormId.VEHICLE_OWNER_COMPANY_DATA];

export const selectVehicleOwnerPersonData = (
  store: RootState
): VehicleOwnerPersonFormInputs =>
  store.ocVehicle.vehicleOwner.data[FormId.VEHICLE_OWNER_PERSON_DATA];

export const selectVehicleOwnerContactPersonData = (
  store: RootState
): VehicleOwnerContactPersonFormInputs =>
  store.ocVehicle.vehicleOwner.data[FormId.VEHICLE_OWNER_CONTACT_PERSON_DATA];

export const selectIsSelectedVehicleCar = createSelector(
  selectVehicleOwnerVehicleData,
  (vehicleData) => vehicleData[VehicleComponentId.VEHICLE_TYPE] === "car"
);

export const selectIsSelectedVehicleOther = createSelector(
  selectVehicleOwnerVehicleData,
  (vehicleData) => vehicleData[VehicleComponentId.VEHICLE_TYPE] === "other"
);

export const selectShouldDisplayCompany = createSelector(
  selectVehicleOwnerOwnerData,
  (ownerData) => ownerData[OwnerComponentId.OWNER_TYPE] === "company"
);

export const selectShouldDisplayPerson = createSelector(
  selectVehicleOwnerOwnerData,
  (ownerData) => ownerData[OwnerComponentId.OWNER_TYPE] === "person"
);

export const selectIsContactPersonOther = createSelector(
  selectVehicleOwnerContactPersonData,
  (contactPerson) =>
    contactPerson[ContactPersonComponentId.PERSON_TYPE] === "otherPerson"
);

const selectVehicleOtherData = createSelector(
  selectVehicleOwnerVehicleData,
  (data) => [
    {
      name: "Rodzaj pojazdu i numer identyfikacyjny",
      value: data[VehicleComponentId.DESCRIPTION],
    },
  ]
);

const selectRegistrationNumber = createSelector(
  selectVehicleOwnerVehicleData,
  (data) => ({
    name: "Numer rejestracyjny pojazdu poszkodowanego",
    value: data[VehicleComponentId.REGISTRATION_NUMBER],
  })
);

const selectVinNumber = createSelector(
  selectVehicleOwnerVehicleData,
  (data) => ({
    name: "Numer VIN",
    value: data[VehicleComponentId.VIN],
  })
);

const selectProductionYear = createSelector(
  selectVehicleOwnerVehicleData,
  (data) => ({
    name: "Rok produkcji",
    value: data[VehicleComponentId.PRODUCTION_YEAR].slice(-4),
  })
);

const selectMakeAndModel = createSelector(
  selectVehicleOwnerVehicleData,
  (data) => ({
    name: "Marka i model",
    value: [data[VehicleComponentId.MAKE], data[VehicleComponentId.MODEL]]
      .join(" ")
      .trim(),
  })
);

const selectRegistrationDate = createSelector(
  selectVehicleOwnerVehicleData,
  (data) => ({
    name: "Data pierwszej rejestracji",
    value: data[VehicleComponentId.REGISTRATION_DATE],
  })
);

const selectVehicleCarData = createSelector(
  selectRegistrationNumber,
  selectVinNumber,
  selectProductionYear,
  selectMakeAndModel,
  selectRegistrationDate,
  (
    registrationNumber,
    vinNumber,
    productionYear,
    makeAndModel,
    registrationDate
  ) => [
    registrationNumber,
    vinNumber,
    productionYear,
    makeAndModel,
    registrationDate,
  ]
);

const selectState = (state: RootState) => state;

const selectVehicleOwnerVehicleSummaryData = createSelector(
  selectIsSelectedVehicleOther,
  selectState,
  (isVehicleOther: boolean, state: RootState) => [
    {
      name: "Typ pojazdu",
      value: isVehicleOther ? "inne" : "pojazd samochodowy",
    },
    ...(isVehicleOther
      ? selectVehicleOtherData(state)
      : selectVehicleCarData(state)),
  ]
);

const selectOwnerPersonNameSurname = createSelector(
  selectVehicleOwnerPersonData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [data[PersonComponentId.NAME], data[PersonComponentId.SURNAME]]
      .join(" ")
      .trim(),
  })
);

const selectOwnerPersonTel = createSelector(
  selectVehicleOwnerPersonData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[PersonComponentId.PHONE]),
  })
);

const selectOwnerPersonEmail = createSelector(
  selectVehicleOwnerPersonData,
  (data) => ({
    name: "Email",
    value: data[PersonComponentId.EMAIL],
  })
);

const selectOwnerPersonPesel = createSelector(
  selectVehicleOwnerPersonData,
  (data) => ({
    name: "PESEL",
    value: data[PersonComponentId.PESEL],
  })
);

const selectOwnerPersonAddress = createSelector(
  selectVehicleOwnerPersonData,
  (data) => ({
    name: "Ulica, kod pocztowy, miejscowość",
    value: generateAddress(data),
  })
);

const selectCoowner = createSelector(selectVehicleOwnerPersonData, (data) => ({
  name: "Czy w dowodzie istnieje współwłaściciel",
  value: data[PersonComponentId.COOWNER] === "true" ? "tak" : "nie",
}));

const selectOwnerPersonData = createSelector(
  selectOwnerPersonNameSurname,
  selectOwnerPersonTel,
  selectOwnerPersonEmail,
  selectOwnerPersonPesel,
  selectOwnerPersonAddress,
  selectCoowner,
  (nameSurname, tel, email, pesel, address, coowner) => [
    nameSurname,
    tel,
    email,
    pesel,
    address,
    coowner,
  ]
);

const selectOwnerCompanyName = createSelector(
  selectVehicleOwnerCompanyData,
  (data) => ({
    name: "Nazwa firmy",
    value: data[CompanyComponentId.NAME],
  })
);

const selectOwnerCompanyRegon = createSelector(
  selectVehicleOwnerCompanyData,
  (data) => ({
    name: "REGON",
    value: data[CompanyComponentId.REGON],
  })
);

const selectOwnerCompanyTel = createSelector(
  selectVehicleOwnerCompanyData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[CompanyComponentId.PHONE]),
  })
);

const selectOwnerCompanyEmail = createSelector(
  selectVehicleOwnerCompanyData,
  (data) => ({
    name: "Email",
    value: data[CompanyComponentId.EMAIL],
  })
);

const selectOwnerCompanyAddress = createSelector(
  selectVehicleOwnerCompanyData,
  (data) => ({
    name: "Ulica, kod pocztowy, miejscowość",
    value: generateAddress(data),
  })
);

const selectOwnerCompanyData = createSelector(
  selectOwnerCompanyName,
  selectOwnerCompanyRegon,
  selectOwnerCompanyTel,
  selectOwnerCompanyEmail,
  selectOwnerCompanyAddress,
  (name, regon, tel, email, address) => [name, regon, tel, email, address]
);

const selectVehicleOwnerOwnerSummaryData = createSelector(
  selectShouldDisplayCompany,
  selectState,
  (isCompany: boolean, state: RootState) => [
    ...(isCompany
      ? selectOwnerCompanyData(state)
      : selectOwnerPersonData(state)),
  ]
);

const selectVehicleOwnerContactPersonType = createSelector(
  selectVehicleOwnerContactPersonData,
  (data) => ({
    name: "Kto jest osobą kontaktową",
    value: mapPersonTypeToLabel[data.type] || "",
  })
);

const selectContactPersonNameSurname = createSelector(
  selectVehicleOwnerContactPersonData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [
      data[ContactPersonComponentId.NAME],
      data[ContactPersonComponentId.SURNAME],
    ]
      .join(" ")
      .trim(),
  })
);

const selectContactPersonCompanyName = createSelector(
  selectVehicleOwnerContactPersonData,
  (data) => ({
    name: "Nazwa firmy",
    value: data[ContactPersonComponentId.COMPANY_NAME],
  })
);

const selectContactPersonTel = createSelector(
  selectVehicleOwnerContactPersonData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[ContactPersonComponentId.PHONE]),
  })
);

const selectContactPersonEmail = createSelector(
  selectVehicleOwnerContactPersonData,
  (data) => ({
    name: "Email",
    value: data[ContactPersonComponentId.EMAIL],
  })
);

const selectVehicleOwnerContactPersonDetailsData = createSelector(
  selectContactPersonNameSurname,
  selectContactPersonCompanyName,
  selectContactPersonTel,
  selectContactPersonEmail,
  (nameSurname, companyName, tel, email) => [
    nameSurname,
    companyName,
    tel,
    email,
  ]
);

const selectVehicleOwnerContactPersonSummaryData = createSelector(
  selectVehicleOwnerContactPersonType,
  selectVehicleOwnerContactPersonDetailsData,
  (type, details) => [type, ...details]
);

export const selectSummaryData = createSelector(
  selectVehicleOwnerVehicleSummaryData,
  selectVehicleOwnerOwnerSummaryData,
  selectVehicleOwnerContactPersonSummaryData,
  (vehicle, owner, contactPerson) => ({ vehicle, owner, contactPerson })
);

export const selectFormData = (store: RootState): FormDataInputs =>
  store.ocVehicle.vehicleOwner.data;

export const selectUIData = (store: RootState): UIState =>
  store.ocVehicle.vehicleOwner.ui;

export const selectVehicleOwnerAvailableCities = (store: RootState) =>
  store.ocVehicle.vehicleOwner.ui.vehicleOwnerAvailableCities || [];

export const selectVehicleOwnerAvailableModels = (store: RootState) =>
  store.ocVehicle.vehicleOwner.ui.vehicleOwnerAvailableModels || [];
